"use client";

import Link from "next/link";
import { Play } from "lucide-react";
import { FAQAccordion } from "@/components/FaqAccordion";

type HomepageSeoFaq = {
  question: string;
  answer: string;
};

export type HomepageSEOContentProps = {
  locale: string;
  title: string;
  intro: string;
  capabilityLine: string;
  useCasesTitle: string;
  useCases: string[];
  popularTemplatesTitle: string;
  popularTemplatesIntro: string;
  popularTemplates: Array<{
    slug: string;
    title: string;
    description: string;
  }>;
  howtoTitle: string;
  howtoSteps: string[];
  videoTitle: string;
  videoDescription: string;
  videoCta: string;
  videoLinkAriaLabel: string;
  videoThumbnailAlt: string;
  videoOpensInNewTab: string;
  featuresTitle: string;
  features: string[];
  faqTitle: string;
  faqs: HomepageSeoFaq[];
  ctaPro: string;
  whyUseTitle: string;
  whyUseReasons: string[];
  vsTitle: string;
  vsComparisons: string[];
  tipsTitle: string;
  tips: string[];
  className?: string;
};

const USE_CASE_ICONS = ["👨‍🎓", "👔", "💪", "💼"];
const FEATURE_ICONS = ["🔄", "⬇️", "📋", "☁️"];
const WHY_USE_ICONS = ["👁️", "🆓", "🎯", "⬇️"];
const VS_ICONS = ["📊", "📱", "📝", "💵"];
const HOMEPAGE_TUTORIAL_VIDEO_ID = "tRus7N8iums";
const HOMEPAGE_TUTORIAL_VIDEO_URL = `https://www.youtube.com/watch?v=${HOMEPAGE_TUTORIAL_VIDEO_ID}`;
const HOMEPAGE_TUTORIAL_VIDEO_THUMBNAIL_URL = `https://img.youtube.com/vi/${HOMEPAGE_TUTORIAL_VIDEO_ID}/hqdefault.jpg`;

export function HomepageSEOContent({
  locale,
  title,
  intro,
  capabilityLine,
  useCasesTitle,
  useCases,
  popularTemplatesTitle,
  popularTemplatesIntro,
  popularTemplates,
  howtoTitle,
  howtoSteps,
  videoTitle,
  videoDescription,
  videoCta,
  videoLinkAriaLabel,
  videoThumbnailAlt,
  videoOpensInNewTab,
  featuresTitle,
  features,
  faqTitle,
  faqs,
  ctaPro,
  whyUseTitle,
  whyUseReasons,
  vsTitle,
  vsComparisons,
  tipsTitle,
  tips,
  className,
}: HomepageSEOContentProps) {
  const getTemplateHref = (slug: string) =>
    locale === "en" ? `/templates/${slug}` : `/${locale}/templates/${slug}`;
  const pricingHref = locale === "en" ? "/pricing" : `/${locale}/pricing`;

  return (
    <section
      aria-labelledby="homepage-seo-title"
      className={`border-t border-gray-200 bg-white px-4 py-10 dark:border-gray-800 dark:bg-gray-950 sm:px-6 lg:px-8 ${className ?? ""}`}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        {/* H1 + 价值主张 */}
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <h1
            id="homepage-seo-title"
            className="text-center text-2xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-3xl"
          >
            {title}
          </h1>
          <p className="text-base leading-7 text-gray-700 dark:text-gray-300">
            {intro}
          </p>
          <p className="text-sm leading-6 text-gray-600 dark:text-gray-400 sm:text-base">
            {capabilityLine}
          </p>
        </div>

        {/* How To */}
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {howtoTitle}
          </h2>
          <div className="grid gap-4 lg:grid-cols-3">
            {howtoSteps.map((step, index) => (
              <div
                key={`howto-${index}`}
                className="rounded-lg border border-gray-200 bg-white p-4 text-center dark:border-gray-700 dark:bg-gray-950"
              >
                <span className="mx-auto mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                  {index + 1}
                </span>
                <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-3 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {videoTitle}
          </h2>
          <p className="mx-auto mb-5 max-w-3xl text-center text-sm leading-6 text-gray-700 dark:text-gray-300 sm:text-base">
            {videoDescription}
          </p>
          <a
            href={HOMEPAGE_TUTORIAL_VIDEO_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={videoLinkAriaLabel}
            className="group block overflow-hidden rounded-lg border border-gray-200 bg-black shadow-sm transition-colors hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-700 dark:hover:border-blue-600 dark:focus:ring-offset-gray-900"
          >
            <div className="relative aspect-video">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={HOMEPAGE_TUTORIAL_VIDEO_THUMBNAIL_URL}
                alt={videoThumbnailAlt}
                width={480}
                height={270}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/92 text-blue-600 shadow-lg transition-transform duration-200 group-hover:scale-105">
                  <Play className="ml-1 size-7 fill-current" aria-hidden="true" />
                </span>
                <span className="rounded-full bg-black/70 px-4 py-2 text-sm font-semibold text-white">
                  {videoCta}
                </span>
                <span className="sr-only">{videoOpensInNewTab}</span>
              </div>
            </div>
          </a>
        </section>

        {/* Why Use */}
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {whyUseTitle}
          </h2>
          <div className="grid gap-2.5 lg:grid-cols-2">
            {whyUseReasons.map((reason, index) => (
              <div
                key={`why-use-${index}`}
                className="flex gap-2.5 text-sm leading-6 text-gray-700 dark:text-gray-300 sm:text-base"
              >
                <span className="mt-0.5 shrink-0 text-base">
                  {WHY_USE_ICONS[index] ?? ""}
                </span>
                <span>{reason}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {useCasesTitle}
          </h2>
          <div className="grid gap-2.5 lg:grid-cols-2">
            {useCases.map((useCase, index) => (
              <div
                key={`use-case-${index}`}
                className="flex gap-2.5 text-sm leading-6 text-gray-700 dark:text-gray-300 sm:text-base"
              >
                <span className="mt-0.5 shrink-0 text-base">
                  {USE_CASE_ICONS[index] ?? ""}
                </span>
                <span>{useCase}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-3 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {popularTemplatesTitle}
          </h2>
          <p className="mx-auto mb-5 max-w-3xl text-center text-sm leading-6 text-gray-700 dark:text-gray-300 sm:text-base">
            {popularTemplatesIntro}
          </p>
          <div className="grid gap-4 lg:grid-cols-3">
            {popularTemplates.map((template) => (
              <Link
                key={template.slug}
                href={getTemplateHref(template.slug)}
                className="rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-blue-300 dark:border-gray-700 dark:bg-gray-950 dark:hover:border-blue-600"
              >
                <span className="block text-sm font-semibold text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400">
                  {template.title}
                </span>
                <span className="mt-2 block text-sm leading-6 text-gray-700 dark:text-gray-300">
                  {template.description}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Schedule Builder vs Other Tools */}
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {vsTitle}
          </h2>
          <div className="grid gap-2.5 lg:grid-cols-2">
            {vsComparisons.map((comparison, index) => (
              <div
                key={`vs-${index}`}
                className="flex gap-2.5 text-sm leading-6 text-gray-700 dark:text-gray-300 sm:text-base"
              >
                <span className="mt-0.5 shrink-0 text-base">
                  {VS_ICONS[index] ?? ""}
                </span>
                <span>{comparison}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 10 Tips */}
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {tipsTitle}
          </h2>
          <div className="grid gap-2.5 lg:grid-cols-2">
            {tips.map((tip, index) => (
              <div
                key={`tip-${index}`}
                className="flex gap-2.5 text-sm leading-6 text-gray-700 dark:text-gray-300 sm:text-base"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                  {index + 1}
                </span>
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {featuresTitle}
          </h2>
          <div className="grid gap-2.5 lg:grid-cols-2">
            {features.map((feature, index) => (
              <div
                key={`feature-${index}`}
                className="flex gap-2.5 text-sm leading-6 text-gray-700 dark:text-gray-300 sm:text-base"
              >
                <span className="mt-0.5 shrink-0 text-base">
                  {FEATURE_ICONS[index] ?? ""}
                </span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Link
              href={pricingHref}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              {ctaPro}
            </Link>
          </div>
        </section>

        {/* FAQ 手风琴 —— 与 pricing / templates 同一个 shadcn 实现（`FAQAccordion`）；
            首页保留自己的卡片外壳，因为相邻区块（How To / Features / Tips）都是这个版式。
            不直接用 `FAQSection` 的是：它自带 py-16 + text-3xl 标题 + 装饰线，会把这里
            撑成与兄弟区块不一致的另一套版式。 */}
        <section className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            {faqTitle}
          </h2>
          <FAQAccordion items={faqs} />
        </section>
      </div>
    </section>
  );
}
